/**
 * Frontend Logger
 * Système de logging complet pour capturer et tracer toutes les erreurs
 * 
 * Usage:
 *   import logger from './utils/logger.js';
 *   logger.info('Message');
 *   logger.error('Error message', error);
 *   logger.warn('Warning');
 *   logger.debug('Debug info', data);
 */

const LOG_LEVELS = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
};

// Niveau minimum de log (configurable via env)
const MIN_LOG_LEVEL = import.meta?.env?.VITE_LOG_LEVEL || 'debug';

// Couleurs pour la console
const COLORS = {
    debug: '#7f8c8d',
    info: '#3498db',
    warn: '#f39c12',
    error: '#e74c3c',
    success: '#27ae60',
};

// Historique des logs pour debug
const logHistory = [];
const MAX_HISTORY = 500;

// Queue pour envoyer les erreurs au backend (pour éviter de surcharger)
const errorQueue = [];
let isSendingErrors = false;
const MAX_QUEUE_SIZE = 50;

/**
 * Récupère les informations du navigateur
 */
function getBrowserInfo() {
    if (typeof window === 'undefined') return null;
    
    const nav = window.navigator || {};
    const screen = window.screen || {};
    
    return {
        name: nav.userAgent?.match(/(?:Chrome|Firefox|Safari|Edge)\/(\d+)/)?.[0] || 'Unknown',
        version: nav.appVersion || 'Unknown',
        platform: nav.platform || 'Unknown',
        language: nav.language || 'Unknown',
        screenResolution: screen.width && screen.height ? `${screen.width}x${screen.height}` : 'Unknown',
        viewportSize: window.innerWidth && window.innerHeight ? `${window.innerWidth}x${window.innerHeight}` : 'Unknown',
    };
}

/**
 * Récupère les informations utilisateur depuis le localStorage ou le token
 */
function getUserInfo() {
    try {
        const token = localStorage.getItem('authToken');
        if (token) {
            // Décoder le token JWT (sans vérification, juste pour extraire les infos)
            const payload = JSON.parse(atob(token.split('.')[1]));
            return {
                userId: payload.sub || payload.id || null,
                userEmail: payload.email || null,
                userRole: payload.role || null,
            };
        }
    } catch (e) {
        // Ignorer les erreurs de décodage
    }
    return {
        userId: null,
        userEmail: null,
        userRole: null,
    };
}

/**
 * Construit l'URL de l'API de manière indépendante (pour éviter la dépendance circulaire)
 */
function getApiUrl(path = '') {
    if (typeof window === 'undefined') return null;
    
    // Essayer d'obtenir l'URL depuis les variables d'environnement ou le localStorage
    const envUrl = import.meta?.env?.VITE_API_URL;
    const storedUrl = localStorage.getItem('apiBaseUrl');
    const defaultUrl = 'https://api.launcher-app.com';
    const localUrl = 'http://localhost:3050';
    
    // Déterminer l'URL de base
    let baseUrl = envUrl || storedUrl || defaultUrl;
    
    // Si on est en localhost, essayer le fallback local
    if (window.location?.hostname === 'localhost' || window.location?.hostname === '127.0.0.1') {
        baseUrl = localUrl;
    }
    
    const sanitizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${sanitizedPath}`;
}

/**
 * Récupère l'environnement actuel de l'API
 */
function getEnvironmentInfo() {
    try {
        // Récupérer l'environnement configuré
        const savedEnv = localStorage.getItem('launcher_admin_api_env') || 'auto';
        
        // Récupérer l'URL de l'API actuelle
        const apiUrl = localStorage.getItem('apiBaseUrl') || 
                      import.meta?.env?.VITE_API_URL || 
                      'http://localhost:3050';
        
        // Déterminer l'environnement effectif si en mode auto
        let effectiveEnv = savedEnv;
        if (savedEnv === 'auto') {
            if (apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1')) {
                effectiveEnv = 'local';
            } else if (apiUrl.includes('staging')) {
                effectiveEnv = 'staging';
            } else {
                effectiveEnv = 'production';
            }
        }
        
        return {
            environment: effectiveEnv,
            apiUrl: apiUrl,
            configMode: savedEnv,
        };
    } catch (e) {
        return {
            environment: 'unknown',
            apiUrl: 'unknown',
            configMode: 'auto',
        };
    }
}

/**
 * Envoie une erreur au backend de manière asynchrone et non bloquante
 */
async function sendErrorToBackend(entry) {
    // Ne pas envoyer les erreurs de niveau debug ou info
    if (entry.level === 'debug' || entry.level === 'info') {
        return;
    }

    try {
        const userInfo = getUserInfo();
        const browserInfo = getBrowserInfo();
        const envInfo = getEnvironmentInfo();
        
        const errorData = {
            level: entry.level,
            message: entry.message,
            errorMessage: entry.args?.[0]?.message || entry.args?.[0]?.error_message || entry.message,
            stack: entry.args?.[0]?.stack || entry.args?.[0]?.error_stack || null,
            component: entry.context || 'unknown',
            context: entry.context || null,
            userId: userInfo.userId,
            userEmail: userInfo.userEmail,
            userRole: userInfo.userRole,
            url: window.location?.href || null,
            referrer: document.referrer || null,
            browserInfo: browserInfo,
            environment: envInfo.environment,
            apiUrl: envInfo.apiUrl,
            environmentConfig: envInfo.configMode,
            file: entry.caller?.file || null,
            line: entry.caller?.line ? parseInt(entry.caller.line, 10) : null,
            column: entry.caller?.column || null,
            function: entry.caller?.function || null,
            metadata: {
                args: entry.args,
                timestamp: entry.timestamp,
            },
            tags: entry.level === 'error' ? ['critical'] : ['warning'],
        };

        // Construire l'URL de l'API
        const apiUrl = getApiUrl('/api/errors');
        if (!apiUrl) {
            return; // Pas d'URL disponible, ignorer silencieusement
        }

        // Récupérer le token d'authentification si disponible
        const token = localStorage.getItem('authToken');
        const headers = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        // Envoyer au backend avec fetch directement (sans dépendance circulaire)
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify(errorData),
        }).catch(() => {
            // Ignorer silencieusement les erreurs d'envoi pour ne pas créer une boucle
            return null;
        });

        if (response && !response.ok) {
            // Log silencieux en cas d'échec
            console.debug('Failed to send error to backend:', response.status);
        }
    } catch (error) {
        // Ignorer complètement les erreurs d'envoi pour éviter les boucles infinies
        console.debug('Error sending to backend (ignored):', error);
    }
}

/**
 * Traite la queue d'erreurs
 */
async function processErrorQueue() {
    if (isSendingErrors || errorQueue.length === 0) {
        return;
    }

    isSendingErrors = true;
    
    while (errorQueue.length > 0) {
        const entry = errorQueue.shift();
        await sendErrorToBackend(entry);
        // Petit délai pour ne pas surcharger le serveur
        await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    isSendingErrors = false;
}

/**
 * Formate la date pour les logs
 */
function formatTimestamp() {
    const now = new Date();
    return now.toISOString();
}

/**
 * Sérialise les objets pour le logging (gère les erreurs, les objets circulaires, etc.)
 */
function serializeArg(arg) {
    if (arg === null) return 'null';
    if (arg === undefined) return 'undefined';

    if (arg instanceof Error) {
        return {
            name: arg.name,
            message: arg.message,
            stack: arg.stack,
            cause: arg.cause ? serializeArg(arg.cause) : undefined,
        };
    }

    if (arg instanceof Response) {
        return {
            type: 'Response',
            url: arg.url,
            status: arg.status,
            statusText: arg.statusText,
            ok: arg.ok,
        };
    }

    if (arg instanceof Request) {
        return {
            type: 'Request',
            url: arg.url,
            method: arg.method,
        };
    }

    if (arg instanceof Event) {
        return {
            type: 'Event',
            eventType: arg.type,
            target: arg.target?.tagName || arg.target?.toString?.() || 'unknown',
        };
    }

    if (typeof arg === 'object') {
        try {
            // Tester si sérialisable
            JSON.stringify(arg);
            return arg;
        } catch (e) {
            // Objet circulaire ou non sérialisable
            try {
                return Object.keys(arg).reduce((acc, key) => {
                    try {
                        acc[key] = typeof arg[key] === 'object' ? '[Object]' : String(arg[key]);
                    } catch {
                        acc[key] = '[Unreadable]';
                    }
                    return acc;
                }, {});
            } catch {
                return '[Circular Object]';
            }
        }
    }

    return arg;
}

/**
 * Extrait le contexte de l'appelant (fichier, ligne)
 */
function getCallerInfo() {
    try {
        const stack = new Error().stack;
        if (!stack) return null;

        const lines = stack.split('\n');
        // Trouver la première ligne qui n'est pas dans logger.js
        for (let i = 2; i < lines.length; i++) {
            const line = lines[i];
            if (!line.includes('logger.js') && !line.includes('Logger')) {
                // Extraire le nom de fichier et la ligne
                const match = line.match(/at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?/);
                if (match) {
                    const [, fnName, file, lineNum] = match;
                    // Nettoyer le chemin du fichier
                    const shortFile = file.split('/').pop() || file;
                    return {
                        function: fnName || 'anonymous',
                        file: shortFile,
                        line: lineNum,
                    };
                }
            }
        }
    } catch {
        // Ignorer les erreurs de parsing du stack
    }
    return null;
}

/**
 * Ajoute une entrée à l'historique
 */
function addToHistory(entry) {
    logHistory.push(entry);
    if (logHistory.length > MAX_HISTORY) {
        logHistory.shift();
    }
}

/**
 * Log générique
 */
function log(level, context, message, ...args) {
    if (LOG_LEVELS[level] < LOG_LEVELS[MIN_LOG_LEVEL]) {
        return;
    }

    const timestamp = formatTimestamp();
    const caller = getCallerInfo();
    const serializedArgs = args.map(serializeArg);

    const entry = {
        timestamp,
        level,
        context,
        message,
        args: serializedArgs,
        caller,
    };

    addToHistory(entry);

    // Formatage console
    const color = COLORS[level] || '#000';
    const prefix = context ? `[${context}]` : '';
    const callerStr = caller ? ` (${caller.file}:${caller.line})` : '';

    const consoleMethod = level === 'debug' ? 'log' : level;

    if (args.length > 0) {
        console[consoleMethod](
            `%c${timestamp} [${level.toUpperCase()}]${prefix}${callerStr}`,
            `color: ${color}; font-weight: bold;`,
            message,
            ...serializedArgs
        );
    } else {
        console[consoleMethod](
            `%c${timestamp} [${level.toUpperCase()}]${prefix}${callerStr}`,
            `color: ${color}; font-weight: bold;`,
            message
        );
    }

    // Envoyer les erreurs au processus principal Electron si disponible
    if (level === 'error' && window.launcherAPI?.logError) {
        try {
            window.launcherAPI.logError(entry);
        } catch {
            // Ignorer si pas disponible
        }
    }

    // Envoyer les erreurs et warnings au backend (en queue pour ne pas bloquer)
    if ((level === 'error' || level === 'warn') && typeof window !== 'undefined') {
        // Ajouter à la queue
        if (errorQueue.length < MAX_QUEUE_SIZE) {
            errorQueue.push(entry);
            // Traiter la queue de manière asynchrone
            processErrorQueue().catch(() => {
                // Ignorer les erreurs de traitement
            });
        }
    }
}

/**
 * Logger principal
 */
const logger = {
    debug: (message, ...args) => log('debug', null, message, ...args),
    info: (message, ...args) => log('info', null, message, ...args),
    warn: (message, ...args) => log('warn', null, message, ...args),
    error: (message, ...args) => log('error', null, message, ...args),

    /**
     * Log avec un contexte spécifique (ex: nom du composant)
     */
    withContext: (context) => ({
        debug: (message, ...args) => log('debug', context, message, ...args),
        info: (message, ...args) => log('info', context, message, ...args),
        warn: (message, ...args) => log('warn', context, message, ...args),
        error: (message, ...args) => log('error', context, message, ...args),
    }),

    /**
     * Log une erreur avec stack trace complet
     */
    logError: (error, context = 'Error') => {
        const errorInfo = serializeArg(error);
        log('error', context, error?.message || 'Unknown error', errorInfo);

        // Log aussi le stack dans un format lisible
        if (error?.stack) {
            console.error(`%c${error.stack}`, 'color: #e74c3c;');
        }
    },

    /**
     * Récupère l'historique des logs
     */
    getHistory: () => [...logHistory],

    /**
     * Efface l'historique
     */
    clearHistory: () => {
        logHistory.length = 0;
    },

    /**
     * Exporte les logs pour debug
     */
    exportLogs: () => {
        const data = JSON.stringify(logHistory, null, 2);
        const blob = new Blob([data], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `launcher-logs-${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
    },

    /**
     * Affiche un résumé des erreurs dans la console
     */
    showErrorSummary: () => {
        const errors = logHistory.filter(e => e.level === 'error');
        console.group(`%cError Summary (${errors.length} errors)`, 'color: #e74c3c; font-weight: bold;');
        errors.forEach((e, i) => {
            console.log(`${i + 1}. [${e.timestamp}] ${e.context || ''}: ${e.message}`);
        });
        console.groupEnd();
    },
};

/**
 * Initialise les handlers d'erreurs globales
 */
export function initGlobalErrorHandlers() {
    const errorLogger = logger.withContext('GlobalError');

    // Erreurs JavaScript non capturées
    window.onerror = (message, source, lineno, colno, error) => {
        errorLogger.error('Uncaught Error', {
            message,
            source,
            line: lineno,
            column: colno,
            error: serializeArg(error),
            stack: error?.stack,
        });

        // Ne pas empêcher le comportement par défaut
        return false;
    };

    // Promesses rejetées non gérées
    window.addEventListener('unhandledrejection', (event) => {
        const reason = event.reason;

        errorLogger.error('Unhandled Promise Rejection', {
            reason: serializeArg(reason),
            message: reason?.message || String(reason),
            stack: reason?.stack,
            promise: '[Promise]',
        });

        // Log le stack si disponible
        if (reason?.stack) {
            console.error('%cUnhandled Rejection Stack:', 'color: #e74c3c; font-weight: bold;');
            console.error(reason.stack);
        }
    });

    // Erreurs de ressources (images, scripts, etc.)
    window.addEventListener('error', (event) => {
        if (event.target && event.target !== window) {
            // C'est une erreur de ressource (img, script, link, etc.)
            const target = event.target;
            errorLogger.warn('Resource Load Error', {
                tagName: target.tagName,
                src: target.src || target.href,
                id: target.id,
                className: target.className,
            });
        }
    }, true); // Capture phase pour intercepter avant bubbling

    // Erreurs de fetch
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
        
        // Skip logging for debug/telemetry URLs (e.g., local ingest endpoints)
        const isDebugTelemetryUrl = url && (
            url.includes('/ingest/') || 
            url.includes('127.0.0.1:7242')
        );

        try {
            const response = await originalFetch(...args);

            // Logger les erreurs HTTP (skip pour les URLs de debug)
            if (!response.ok && response.status >= 400 && !isDebugTelemetryUrl) {
                logger.withContext('Fetch').warn(`HTTP ${response.status}`, {
                    url,
                    status: response.status,
                    statusText: response.statusText,
                });
            }

            return response;
        } catch (error) {
            // Skip logging network errors for debug/telemetry URLs
            if (!isDebugTelemetryUrl) {
                logger.withContext('Fetch').error('Network Error', {
                    url,
                    error: serializeArg(error),
                });
            }
            throw error;
        }
    };

    // Sécurité: Intercepter les clics sur les liens morts
    document.addEventListener('click', (event) => {
        const target = event.target.closest('a');
        if (target && target.href && target.href.startsWith('javascript:void')) {
            logger.withContext('Navigation').debug('Dead link clicked', {
                href: target.href,
                text: target.textContent?.substring(0, 50),
            });
        }
    });

    // Console override pour capturer les console.error existants
    const originalConsoleError = console.error;
    console.error = (...args) => {
        // Ajouter à l'historique
        addToHistory({
            timestamp: formatTimestamp(),
            level: 'error',
            context: 'console.error',
            message: args[0],
            args: args.slice(1).map(serializeArg),
        });

        // Appeler l'original
        originalConsoleError.apply(console, args);
    };

    logger.info('🔧 Global error handlers initialized');
    logger.info(`📝 Log level: ${MIN_LOG_LEVEL}`);
    logger.info('💡 Use logger.showErrorSummary() to see all errors');
    logger.info('💾 Use logger.exportLogs() to download log file');
}

// Exposer le logger sur window pour le debug en console
if (typeof window !== 'undefined') {
    window.__LAUNCHER_LOGGER__ = logger;
}

export default logger;

